import axios from "axios";
import {
  GET_DETAILS_ERROR,
  GET_DETAILS_SUCCESS,
  GET_PORTFOLIO_ERROR,
  GET_PORTFOLIO_REQUEST,
  GET_PORTFOLIO_SUCCESS,
  UPDATE_REVIEW_DATE_ERROR,
  UPDATE_REVIEW_DATE_REQUEST,
  UPDATE_REVIEW_DATE_SUCCESS,
  UPDATE_WORKLOAD_EDITION_ERROR,
  UPDATE_WORKLOAD_EDITION_REQUEST,
  UPDATE_WORKLOAD_EDITION_SUCCESS,
  UPDATE_AUTO_FEED_ACTUALS_ERROR,
  UPDATE_AUTO_FEED_ACTUALS_REQUEST,
  UPDATE_AUTO_FEED_ACTUALS_SUCCESS,
  UPDATE_DETAILS_ERROR,
  UPDATE_DETAILS_SUCCESS,
  UPDATE_DETAILS_REQUEST,
  GET_PORTFOLIO_OPTIONS,
} from "../types";
import { logout } from "../security-services/SecurityActions";
import logoutforSpecificErrorCodes from "../../securityUtils/LogoutUtils";
import { toast } from "react-toastify";
import { PDA_PREFIX, PORTFOLIO } from "../../global/constants";
import { setDisplayUnitSettings } from "../../utils/displayUnits";
import { setSchedulingCalendarSettings } from "../../utils/schedulingCalendar";

export const updateReviewDateRequest = () => {
  return {
    type: UPDATE_REVIEW_DATE_REQUEST,
  };
};

export const updateReviewDateSuccess = (newReviewDate) => {
  return {
    type: UPDATE_REVIEW_DATE_SUCCESS,
    payload: newReviewDate,
  };
};

export const updateReviewDateError = (error) => {
  return {
    type: UPDATE_REVIEW_DATE_ERROR,
    payload: error,
  };
};

export const updateWorkloadEditionStateRequest = () => {
  return {
    type: UPDATE_WORKLOAD_EDITION_REQUEST,
  };
};

export const updateWorkloadEditionStateSuccess = (payload) => {
  return {
    type: UPDATE_WORKLOAD_EDITION_SUCCESS,
    payload,
  };
};

export const updateWorkloadEditionStateError = (error) => {
  return {
    type: UPDATE_WORKLOAD_EDITION_ERROR,
    payload: error,
  };
};

export const getDetailsRequest = () => {
  return {
    type: GET_DETAILS_SUCCESS,
  };
};

export const getDetailsSuccess = (details) => {
  return {
    type: GET_DETAILS_SUCCESS,
    payload: details,
  };
};

export const getDetailsError = (error) => {
  return {
    type: GET_DETAILS_ERROR,
    payload: error,
  };
};

export const getPortfolioRequest = () => {
  return {
    type: GET_PORTFOLIO_REQUEST,
  };
};

export const getPortfolioSuccess = (portfolio) => {
  return {
    type: GET_PORTFOLIO_SUCCESS,
    payload: portfolio,
  };
};

export const getPortfolioError = (error) => {
  return {
    type: GET_PORTFOLIO_ERROR,
    payload: error,
  };
};

export const getPortfolio = () => async (dispatch) => {
  dispatch(getPortfolioRequest());

  try {
    const portfolioData = localStorage.getItem(PORTFOLIO);

    if (portfolioData) {
      const parsedData = JSON.parse(portfolioData);
      dispatch(getPortfolioSuccess(parsedData));
    } else {
      const res = await axios.get(`${PDA_PREFIX}/portfolio`);
      localStorage.setItem(PORTFOLIO, JSON.stringify(res.data));
      dispatch(getPortfolioSuccess(res.data));
    }
  } catch (error) {
    if (logoutforSpecificErrorCodes(error)) {
      dispatch(logout());
    }
    dispatch(getPortfolioError(error));
  }
};

export const getDetails = () => async (dispatch) => {
  try {
    const res = await axios.get(`${PDA_PREFIX}/portfolio/details`);
    // Feed the Gantt display-unit cache before any component consumes
    // the details — column configs are built during first render, ahead
    // of any component effect.
    setDisplayUnitSettings(res.data);
    setSchedulingCalendarSettings(res.data);
    dispatch(getDetailsSuccess(res.data));
  } catch (error) {
    if (logoutforSpecificErrorCodes(error)) {
      dispatch(logout());
    }
    dispatch(getDetailsError(error));
  }
};

/**
 * Update portfolio review date using POST /portfolio/update/review-date.
 * @param {number} portfolioId - The portfolio ID
 * @param {string} newReviewDate - The new review date in YYYY-MM-DD format
 * @returns {Promise<{portfolioId: number, reviewDate: string, affectedProjectCount: number}>}
 * @throws {AxiosError} Re-throws the error after dispatching so callers can handle it
 */
export const updatePortfolioReviewDate = (portfolioId, newReviewDate) => async (dispatch) => {
  dispatch(updateReviewDateRequest());
  try {
    const response = await axios.post(
      `${PDA_PREFIX}/portfolio/update/review-date`,
      { portfolioId, newReviewDate },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    dispatch(updateReviewDateSuccess(newReviewDate));
    return response.data;
  } catch (error) {
    if (logoutforSpecificErrorCodes(error)) {
      dispatch(logout());
    }
    dispatch(updateReviewDateError(error));
    throw error;
  }
};

export const updateWorkloadEditionState = (isWorkloadEditionEnabled) => async (
  dispatch
) => {
  dispatch(updateWorkloadEditionStateRequest());
  try {
    const res = await axios.put(
      `${PDA_PREFIX}/portfolio/update/workload-edition`,
      isWorkloadEditionEnabled,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    dispatch(updateWorkloadEditionStateSuccess(res.data));
    toast.success(
      <div>
        Team workload edition session has been{" "}
        <b>{isWorkloadEditionEnabled ? "OPENED" : "CLOSED"}</b> successfully.
      </div>
    );
  } catch (error) {
    if (logoutforSpecificErrorCodes(error)) {
      dispatch(logout());
    }
    dispatch(updateWorkloadEditionStateError(error));
  }
};

export const updateAutoFeedActualsRequest = () => ({
  type: UPDATE_AUTO_FEED_ACTUALS_REQUEST,
});

export const updateAutoFeedActualsSuccess = (payload) => ({
  type: UPDATE_AUTO_FEED_ACTUALS_SUCCESS,
  payload,
});

export const updateAutoFeedActualsError = (error) => ({
  type: UPDATE_AUTO_FEED_ACTUALS_ERROR,
  payload: error,
});

export const updateAutoFeedActualsEnabled = (enabled) => async (dispatch) => {
  dispatch(updateAutoFeedActualsRequest());
  try {
    const res = await axios.put(
      `${PDA_PREFIX}/portfolio/update/auto-feed-actuals`,
      enabled,
      { headers: { "Content-Type": "application/json" } }
    );
    dispatch(updateAutoFeedActualsSuccess(res.data));
    toast.success(
      <div>
        Auto-Feed Actuals has been{" "}
        <b>{res.data ? "ENABLED" : "DISABLED"}</b>.
      </div>
    );
  } catch (error) {
    if (logoutforSpecificErrorCodes(error)) {
      dispatch(logout());
    }
    dispatch(updateAutoFeedActualsError(error.message));
    toast.error("Failed to update Auto-Feed Actuals setting.");
  }
};

//Settings update
export const updateDetailsRequest = () => {
  return {
    type: UPDATE_DETAILS_REQUEST,
  };
};

export const updateDetailsSuccess = (details) => {
  return {
    type: UPDATE_DETAILS_SUCCESS,
    payload: details,
  };
};

export const updateDetailsError = (error) => {
  return {
    type: UPDATE_DETAILS_ERROR,
    payload: error,
  };
};

/**
 * Saves a partial settings payload — the Settings page sends one field at a
 * time. Rethrows so the caller can roll the field back to the persisted value;
 * success is reported by the field's inline indicator, not a toast (one toast
 * per keystroke-debounce would be unusable).
 */
export const updateDetails = (details) => async (dispatch) => {
  dispatch(updateDetailsRequest());
  try {
    const res = await axios.put(`${PDA_PREFIX}/portfolio/details`, details, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    setDisplayUnitSettings(res.data);
    setSchedulingCalendarSettings(res.data);
    // getPortfolio() replays this snapshot into portfolio.details, so leaving it
    // stale silently reverts what was just saved — an auto-logout of "None" came
    // back as the old timeout the next time a page dispatched getPortfolio().
    localStorage.removeItem(PORTFOLIO);
    dispatch(updateDetailsSuccess(res.data));
    return res.data;
  } catch (error) {
    if (logoutforSpecificErrorCodes(error)) {
      dispatch(logout());
    }
    dispatch(updateDetailsError(error.message));
    throw error;
  }
};

export const getPortfolioOptions = () => async (dispatch) => {
  dispatch(getPortfolioRequest());
  try {
    const options = await axios.get(`${PDA_PREFIX}/portfolio/all/selectable`);
    dispatch({
      type: GET_PORTFOLIO_OPTIONS,
      payload: options.data,
    });
  } catch (error) {
    if (logoutforSpecificErrorCodes(error)) {
      dispatch(logout());
    }
    dispatch(getPortfolioError(error));
  }
};
